import React, { useRef, useState } from 'react';
import { useReducedMotion } from '../hooks';
import { useRipples, RippleLayer } from './useRipples';
import './worlds.css';

/**
 * BubbleWorld — translucent bubbles drift upward; tap one to pop it with a soft
 * ripple, and a fresh bubble floats up a moment later. Bounded to a handful of
 * bubbles so the scene stays calm and cheap. Bubbles are buttons (keyboard-
 * poppable, focusable). Under reduced motion they sit still but still pop.
 */
interface Bubble {
  id: number;
  left: number;
  bottom: number;
  size: number;
  hue: number;
  dur: number;
  delay: number;
}

const rnd = (a: number, b: number) => a + Math.random() * (b - a);
const makeBubble = (id: number): Bubble => ({
  id,
  left: rnd(6, 86),
  bottom: rnd(14, 62),
  size: rnd(34, 72),
  hue: Math.round(rnd(158, 205)),
  dur: rnd(4, 8),
  delay: rnd(0, 3),
});

export const BubbleWorld: React.FC = () => {
  const reduced = useReducedMotion();
  const seq = useRef(7);
  const [bubbles, setBubbles] = useState<Bubble[]>(() => Array.from({ length: 7 }, (_, i) => makeBubble(i)));
  const { ripples, spawnAtElement } = useRipples();

  const pop = (bubble: Bubble, el: HTMLElement) => {
    spawnAtElement(el);
    setBubbles((p) => p.filter((b) => b.id !== bubble.id));
    const nid = seq.current++;
    window.setTimeout(() => setBubbles((p) => (p.length < 8 ? [...p, makeBubble(nid)] : p)), 1100);
  };

  return (
    <div className={`mm-world mm-world--bubbles ${reduced ? 'is-still' : ''}`}>
      {bubbles.map((b) => (
        <button
          key={b.id}
          type="button"
          className="mm-bubble"
          aria-label="Pop bubble"
          onClick={(e) => pop(b, e.currentTarget)}
          style={{
            left: `${b.left}%`,
            bottom: `${b.bottom}%`,
            width: b.size,
            height: b.size,
            animationDuration: `${b.dur}s`,
            animationDelay: `${b.delay}s`,
            ['--hue' as string]: b.hue,
          }}
        />
      ))}
      <RippleLayer ripples={ripples} />
      <p className="mm-world__hint">Tap the bubbles ✨</p>
    </div>
  );
};

export default BubbleWorld;
